import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, MapPin, Phone, MessageCircle, ChevronLeft } from "lucide-react";
import { SERVICE_PAGES } from "@/servicePages";
import { JsonLd } from "@/seo/JsonLd";
import {
  BUSINESS,
  servicePageSchema,
  breadcrumbSchema,
} from "@/seo/schemaData";

const ANCHOR_TO_WHATSAPP = {
  "#booking-fisio": { num: "527712141634", display: "+52 771 214 1634" },
  "#booking-alt": { num: "527711610609", display: "+52 771 161 0609" },
  "#booking-estetica": { num: "527711290073", display: "+52 771 129 0073" },
};

export const ServicePage = () => {
  const { slug } = useParams();
  const page = SERVICE_PAGES.find((p) => p.slug === slug);

  useEffect(() => {
    if (page) {
      document.title = page.metaTitle;
      const setMeta = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
        el.setAttribute("content", content);
      };
      setMeta("description", page.metaDesc);
      setMeta("keywords", page.keywords);
      window.scrollTo(0, 0);
    }
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  const wa = ANCHOR_TO_WHATSAPP[page.cta.anchor] || ANCHOR_TO_WHATSAPP["#booking-fisio"];
  const WHATSAPP_URL = `https://wa.me/${wa.num}`;

  const pageUrl = `${BUSINESS.url}/servicios/${page.slug}`;
  const schemas = [
    servicePageSchema(page),
    breadcrumbSchema([
      { name: "Inicio", url: BUSINESS.url + "/" },
      { name: "Servicios", url: BUSINESS.url + "/#services-quiro" },
      { name: page.title, url: pageUrl },
    ]),
  ];

  return (
    <article className="min-h-screen bg-white" data-testid={`service-page-${page.slug}`}>
      <JsonLd id={`service-${page.slug}`} data={schemas} />
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-green-700">
            <ChevronLeft className="h-4 w-4" /> TERAFIQUIRO Wellness Center
          </Link>
          <a href={`/${page.cta.anchor.replace("#", "#")}`} onClick={(e) => { e.preventDefault(); window.location.href = `/${page.cta.anchor}`; }}
             className="inline-flex items-center rounded-full bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700">
            Agendar cita
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-green-100/60 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] uppercase text-green-700">
              <MapPin className="h-3.5 w-3.5" /> Pachuca de Soto · Hidalgo
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              {page.headline}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{page.subheadline}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/" onClick={() => setTimeout(() => { document.getElementById(page.cta.anchor.slice(1))?.scrollIntoView({ behavior: "smooth" }); }, 100)}
                    className="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-6 py-3.5 text-sm font-semibold hover:bg-green-700 shadow-sm">
                {page.cta.text} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-white text-green-700 px-6 py-3.5 text-sm font-semibold hover:bg-green-50">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-xl shadow-green-900/10">
              <img src={page.cover} alt={page.headline} className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions + Techniques */}
      <section className="py-20 bg-green-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-white border border-green-100 p-7 shadow-sm">
            <h2 className="font-display text-2xl font-bold text-slate-900">Padecimientos que tratamos</h2>
            <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
              {page.conditions.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-green-600 text-white">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-green-600 to-emerald-700 text-white p-7 shadow-xl shadow-green-900/10">
            <h2 className="font-display text-2xl font-bold">Técnicas que aplicamos</h2>
            <ul className="mt-5 space-y-2.5">
              {page.techniques.map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-green-50/95">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/15 border border-white/30">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Long-form sections */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {page.sections.map((s) => (
            <div key={s.h2} className="mt-12 first:mt-0">
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">{s.h2}</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-700">{s.body}</p>
            </div>
          ))}

          <div className="mt-16 rounded-3xl bg-gradient-to-br from-green-600 to-emerald-700 p-8 text-white text-center">
            <h3 className="font-display text-2xl font-bold">{page.cta.text}</h3>
            <p className="mt-2 text-sm text-green-50/90">En Pachuca, Hidalgo · Calle El Pípila 200, Col. Centro</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/" onClick={() => setTimeout(() => { document.getElementById(page.cta.anchor.slice(1))?.scrollIntoView({ behavior: "smooth" }); }, 100)}
                    className="inline-flex items-center rounded-full bg-white text-green-700 px-6 py-3 text-sm font-semibold hover:bg-green-50">
                Agendar cita
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-full bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1ebe5d]">
                <Phone className="h-4 w-4 mr-1" /> {wa.display}
              </a>
            </div>
          </div>

          {/* Internal links to other service pages */}
          <div className="mt-16">
            <h3 className="font-display text-lg font-semibold text-slate-900">Otros servicios en Pachuca</h3>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {SERVICE_PAGES.filter((p) => p.slug !== page.slug).slice(0, 4).map((p) => (
                <Link key={p.slug} to={`/servicios/${p.slug}`} className="rounded-2xl border border-slate-100 bg-white p-4 hover:border-green-300 hover:shadow-md transition-all">
                  <p className="font-display font-semibold text-slate-900 text-sm">{p.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{p.subheadline}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default ServicePage;
